/**
 * Extracts attendee ID and unlock tag from a URL or plain attendee ID string
 *
 * Supports the following formats:
 * - https://storibox.com/?attId=ASMG-YKXL-PQZR-0001&u=mytag
 * - https://storibox.com/?attId=ASMG-YKXL-PQZR-0001
 * - ASMG-YKXL-PQZR-0001 (plain attendee ID)
 *
 * @param {string} url - URL with query parameters or plain attendee ID
 * @returns {Object} Object with attendeeId and unlockTag properties
 * @returns {string} returns.attendeeId - The extracted attendee ID
 * @returns {string|null} returns.unlockTag - The unlock tag if present, null otherwise
 */
export function extractAttId(url) {
  // Check if it's a URL with query parameters
  if (url.includes('?')) {
    try {
      // Extract query string and parse parameters
      const queryString = url.substring(url.indexOf('?') + 1);
      const params = new URLSearchParams(queryString);

      const attendeeId = params.get('attId');
      const unlockTag = params.get('u');

      // If attId exists in URL, return it with the unlock tag (or null if not present)
      if (attendeeId) {
        return { attendeeId, unlockTag };
      }
    } catch (error) {
      console.error('Error parsing URL:', error);
    }
  }

  // Just the attendee ID without URL (e.g., ASMG-YKXL-PQZR-0001)
  return { attendeeId: url, unlockTag: null };
}

export function tryJson(str, defaultVal) {
  try {
    return JSON.parse(str);
  } catch {
    return defaultVal;
  }
}

/** Clears the debounce timer and resets timerId on the request state. */
function clearTimer(requestState) {
  if (!requestState.timerId) return;
  clearTimeout(requestState.timerId);
  requestState.timerId = null;
}

/**
 * Creates a debounced state-sync manager that batches rapid changes and sends only the latest state.
 *
 * Use for UI-driven updates (e.g. toggles) where rapid clicks should result in a single request.
 * Each call to schedule() resets the delay; the actual request runs after activity settles.
 * On error, reverts to the last known server state.
 *
 * @param {Object} options
 * @param {number} options.delayMs - Debounce delay before sending the request
 * @param {Function} options.sendRequest - Async sender: ({ key, state, payload }, callback) => void
 * @param {Function} [options.onError] - Optional error handler
 * @returns {{ schedule: Function }} Object with schedule() to enqueue state changes
 *
 * @example
 * const manager = latestStateRequestManager({
 *   delayMs: 5000,
 *   sendRequest: ({ key, state }, done) => api.update(key, state, done),
 *   onError: (err) => alert(err.message),
 * });
 * manager.schedule({ key: 'photo-1', serverState: false, desiredState: true, revertState: (s) => setUI(s) });
 */
export function latestStateRequestManager({ delayMs, sendRequest, onError }) {
  const requestStateByKey = new Map();

  /** Removes entry from map when no pending work and desired === server state. */
  function cleanupIfIdle(key, requestState) {
    if (requestState.isSending || requestState.timerId) return;
    if (requestState.desiredState === requestState.serverState) {
      requestStateByKey.delete(key);
    }
  }

  /** Sends the current desired state to the server. Re-schedules if desired changed while sending. */
  function flush(key) {
    const requestState = requestStateByKey.get(key);
    if (!requestState || requestState.isSending) return;
    clearTimer(requestState);

    // Nothing to send – desired already matches server
    if (requestState.desiredState === requestState.serverState) {
      cleanupIfIdle(key, requestState);
      return;
    }

    const targetState = requestState.desiredState;
    requestState.isSending = true;
    sendRequest({ key, state: targetState, payload: requestState.payload }, (err) => {
      const latestRequestState = requestStateByKey.get(key);
      if (!latestRequestState) return;
      latestRequestState.isSending = false;

      if (err) {
        latestRequestState.desiredState = latestRequestState.serverState;
        if (latestRequestState.revertState) {
          latestRequestState.revertState(latestRequestState.serverState);
        }
        clearTimer(latestRequestState);
        cleanupIfIdle(key, latestRequestState);
        if (onError) onError(err);
        return;
      }

      latestRequestState.serverState = targetState;
      // More changes happened while in-flight – schedule another flush
      if (latestRequestState.desiredState !== latestRequestState.serverState) {
        latestRequestState.timerId = setTimeout(() => flush(key), delayMs);
        return;
      }
      cleanupIfIdle(key, latestRequestState);
    });
  }

  /**
   * Schedules a state change for the given key.
   * Debounces rapid calls; only the latest desiredState is sent.
   *
   * @param {string} key - Unique id for this state (e.g. resource id)
   * @param {*} serverState - Last confirmed server state
   * @param {*} desiredState - Target state to sync
   * @param {*} [payload] - Extra data passed to sendRequest
   * @param {Function} [revertState] - Called on error to revert UI to serverState
   */
  function schedule({ key, serverState, desiredState, payload, revertState }) {
    let requestState = requestStateByKey.get(key);
    if (!requestState) {
      requestState = {
        serverState,
        desiredState: serverState,
        payload: null,
        revertState: null,
        timerId: null,
        isSending: false,
      };
      requestStateByKey.set(key, requestState);
    }
    requestState.payload = payload;
    requestState.revertState = revertState;
    requestState.desiredState = desiredState;
    if (requestState.isSending) return; // Request in flight – flush callback will re-schedule if needed
    clearTimer(requestState);
    requestState.timerId = setTimeout(() => flush(key), delayMs);
  }

  return { schedule };
}
